/* import __COLOCATED_TEMPLATE__ from './schedule-frequency-item.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { cached } from 'tracked-toolbox';
import { ScheduleFrequencyHelpers } from 'embercom/utils/schedule-frequency-helpers';
import type { DayPosition, ExportRule } from 'embercom/models/reporting/dataset-export-schedule';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  exportRule?: ExportRule;
  timezone: string;
  onApply: (rule: ExportRule) => void;
  onCancel: () => void;
  onSelect: () => void;
  isSelected: boolean;
  isPreset: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

/**
 * Generic schedule frequency item component.
 * Provides UI for customizing schedule frequency (weekly/monthly intervals, days, positions).
 *
 * Uses hardcoded translation path 'reporting.schedule-delivery' for all translations.
 *
 * @arg exportRule - The export rule being edited
 * @arg timezone - Timezone for the schedule
 * @arg onApply - Callback when user applies changes
 * @arg onCancel - Callback when user cancels
 * @arg onSelect - Callback when item is selected
 * @arg isSelected - Whether this frequency option is currently selected
 * @arg isPreset - Whether this is a preset frequency (vs custom)
 */
export default class ScheduleFrequencyItem extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare datasetExportScheduleService: DatasetExportScheduleService;

  exportRuleSnapshot?: ExportRule;
  @tracked localExportRule?: ExportRule;
  @tracked intervalInputValue = '';

  private readonly translationBasePath = 'reporting.schedule-delivery';

  get selectedCadence() {
    return this.localExportRule?.interval.step;
  }

  @cached
  get frequencies() {
    return ScheduleFrequencyHelpers.getFrequencies(
      this.intl,
      `${this.translationBasePath}.custom-frequency`,
    );
  }

  @cached
  get daysOfWeek() {
    if (!this.localExportRule) {
      return [];
    }
    return ScheduleFrequencyHelpers.getDaysOfWeek(this.intl, this.localExportRule);
  }

  @action
  onDaysSelectionChange(value: number[]) {
    if (!this.localExportRule) {
      return;
    }
    this.localExportRule = ScheduleFrequencyHelpers.updateDaysSelection(
      this.localExportRule,
      value,
    );
  }

  @action
  onStepSelectionChange(value: string) {
    if (!this.localExportRule) {
      return;
    }
    this.localExportRule = ScheduleFrequencyHelpers.updateStepSelection(
      this.localExportRule,
      value,
    );
  }

  get isMonthlyStep() {
    return this.localExportRule?.interval.step === 'monthly';
  }

  @cached
  get selectedDaysLabel() {
    if (!this.localExportRule) {
      return '';
    }
    return ScheduleFrequencyHelpers.getSelectedDaysLabel(this.intl, this.localExportRule);
  }

  @action
  updateIntervalValue(e: Event) {
    let value = (e.target as HTMLInputElement).value;
    if (!this.localExportRule) {
      return;
    }

    this.intervalInputValue = value;
    this.localExportRule = ScheduleFrequencyHelpers.updateIntervalValue(
      this.localExportRule,
      value,
    );
  }

  get applyButtonDisabled() {
    return ScheduleFrequencyHelpers.isApplyButtonDisabled(this.intervalInputValue);
  }

  @action
  onShow() {
    if (!this.args.exportRule) {
      return;
    }
    this.exportRuleSnapshot = this.args.exportRule;
    this.localExportRule = ScheduleFrequencyHelpers.initializeLocalExportRule(
      this.args.exportRule,
      this.args.isPreset,
      this.datasetExportScheduleService,
      this.args.timezone,
    );
    this.intervalInputValue = String(this.localExportRule.interval.value || '');
  }

  @action
  onApply() {
    if (!this.localExportRule) {
      return;
    }
    this.args.onApply(this.localExportRule);
    this.args.onSelect();
  }

  @action
  onCancel() {
    this.args.onCancel();
  }

  @action
  onDayPositionChange(value: DayPosition[]) {
    if (!this.localExportRule) {
      return;
    }
    this.localExportRule = ScheduleFrequencyHelpers.updateDayPosition(
      this.localExportRule,
      value[0],
    );
  }

  @cached
  get selectedDayPositionLabel() {
    if (!this.localExportRule) {
      return '';
    }
    return ScheduleFrequencyHelpers.getSelectedDayPositionLabel(
      this.intl,
      `${this.translationBasePath}.day-position`,
      this.localExportRule,
    );
  }

  @cached
  get dayPositionItems() {
    if (!this.localExportRule) {
      return [];
    }
    return ScheduleFrequencyHelpers.getDayPositionItems(
      this.intl,
      `${this.translationBasePath}.day-position`,
      this.localExportRule,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ScheduleFrequencyItem': typeof ScheduleFrequencyItem;
    'reporting/custom/schedule-frequency-item': typeof ScheduleFrequencyItem;
  }
}
